import { planConfigs } from "@postdaily/api/services/plan-limits";

import type { PlatformId } from "@/components/social-accounts/provider-icon";
import type { IsoDate } from "@/lib/content-date";
import { joinWithAnd } from "@/lib/join-with-and";

/**
 * Limits reference pages: `/limits` and `/limits/<network>`.
 *
 * "youtube shorts length", "tiktok video size" and "pinterest pin size" are the
 * largest searches this site can win, and the pages that rank for them mostly
 * copy each other. These say where each number comes from: every row names its
 * source and the date it was read, and a row PostDaily enforces says what
 * PostDaily does, which `limits-data.test.ts` checks against the adapters'
 * own constants.
 *
 * Plan caps (media per post, file size per upload) are read from the plans data
 * so a page can't quote an old number.
 */

export type LimitSource = { label: string; url: string };

export type LimitRow = {
  /** What is limited, e.g. "Video length". */
  item: string;
  /** What the network allows, per `source`. */
  network: string;
  /** What PostDaily enforces or allows, when it differs or matters. */
  postDaily?: string;
  source: LimitSource;
  checkedOn: IsoDate;
};

export type LimitsSection = { heading: string; body: string[] };

export type LimitsPage = {
  slug: "instagram" | "youtube-shorts" | "tiktok" | "pinterest";
  platform: PlatformId;
  /** Short name for the hub card and breadcrumbs. */
  name: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  rows: LimitRow[];
  sections: LimitsSection[];
  faqs: { q: string; a: string }[];
  /** The network's landing page, for the "Schedule it" link. */
  landing:
    | "/instagram-scheduler"
    | "/youtube-scheduler"
    | "/tiktok-scheduler"
    | "/pinterest-scheduler";
};

const MB = 1024 * 1024;
const PLAN_CODES = ["free", "starter", "pro", "agency"] as const;

function perPlan(value: (code: (typeof PLAN_CODES)[number]) => string) {
  return joinWithAnd(
    PLAN_CODES.map((code) => `${value(code)} on ${planConfigs[code].name}`),
  );
}

const UPLOAD_CAP = perPlan(
  (code) => `${planConfigs[code].limits.maxMediaFileBytes / MB} MB`,
);
const MEDIA_PER_POST = perPlan((code) =>
  String(planConfigs[code].limits.maxPostMediaItems),
);
const LARGEST_UPLOAD = `${planConfigs.agency.limits.maxMediaFileBytes / MB} MB`;

const CHECKED: IsoDate = "2026-09-27";

const YOUTUBE_SHORTS_HELP: LimitSource = {
  label: "YouTube Help: Understand three-minute YouTube Shorts",
  url: "https://support.google.com/youtube/answer/15424877",
};
const INSTAGRAM_MEDIA_REFERENCE: LimitSource = {
  label: "Meta for Developers: IG User Media reference",
  url: "https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/reference/ig-user/media",
};
const INSTAGRAM_CONTENT_PUBLISHING: LimitSource = {
  label: "Meta for Developers: Instagram content publishing",
  url: "https://developers.facebook.com/docs/instagram-platform/content-publishing/",
};
const INSTAGRAM_HASHTAG_REPORT: LimitSource = {
  label: "Social Media Today: Instagram implements new limits on hashtag use",
  url: "https://www.socialmediatoday.com/news/instagram-implements-new-limits-on-hashtag-use/808309/",
};
const POSTDAILY_INSTAGRAM: LimitSource = {
  label: "PostDaily Instagram scheduler",
  url: "https://www.postdaily.app/instagram-scheduler",
};
const YOUTUBE_UPLOAD_HELP: LimitSource = {
  label: "YouTube Help: Upload videos longer than 15 minutes",
  url: "https://support.google.com/youtube/answer/71673",
};
const YOUTUBE_API: LimitSource = {
  label: "YouTube Data API: Videos resource",
  url: "https://developers.google.com/youtube/v3/docs/videos",
};
const TIKTOK_MEDIA: LimitSource = {
  label: "TikTok for Developers: Media transfer guide",
  url: "https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide",
};
const TIKTOK_DIRECT_POST: LimitSource = {
  label: "TikTok for Developers: Direct Post reference",
  url: "https://developers.tiktok.com/doc/content-posting-api-reference-direct-post",
};
const TIKTOK_PHOTO_POST: LimitSource = {
  label: "TikTok for Developers: Photo Post reference",
  url: "https://developers.tiktok.com/doc/content-posting-api-reference-photo-post",
};
const PINTEREST_SPECS: LimitSource = {
  label: "Pinterest Business help: Product specs",
  url: "https://help.pinterest.com/en/business/article/pinterest-product-specs",
};
const POSTDAILY_API: LimitSource = {
  label: "Pinterest API, as enforced by PostDaily",
  url: "https://developers.pinterest.com/docs/api/v5/",
};

export const LIMITS_PAGES = {
  instagram: {
    slug: "instagram",
    platform: "instagram",
    name: "Instagram",
    metaTitle: "Instagram Caption, Hashtag & Post Limits (2026) — PostDaily",
    metaDescription:
      "Instagram's limits: 2,200-character captions, 5 hashtags, 10-item carousels, Reel and Story lengths and the daily publishing cap, each with its source.",
    h1: "Instagram limits: captions, hashtags, carousels and video",
    intro:
      "A caption holds 2,200 characters and, since December 2025, five hashtags. Here is every Instagram limit that decides whether a post goes out, with the page each number comes from, including two places where Meta's own documents disagree.",
    rows: [
      {
        item: "Caption",
        network: "2,200 characters",
        postDaily: "2,200 characters",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Hashtags",
        network:
          "5 per post or Reel caption (Instagram, 18 December 2025); Meta's API reference still says 30",
        postDaily: "Not counted; Instagram enforces its own cap",
        source: INSTAGRAM_HASHTAG_REPORT,
        checkedOn: CHECKED,
      },
      {
        item: "@ mentions in a caption",
        network: "20",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Carousel",
        network: "Up to 10 images, videos or a mix",
        postDaily: `Up to 10 photos (adding a video makes it a Reel), within your plan's media per post: ${MEDIA_PER_POST}`,
        source: INSTAGRAM_CONTENT_PUBLISHING,
        checkedOn: CHECKED,
      },
      {
        item: "Feed image",
        network:
          "JPEG only, up to 8 MB, 4:5 to 1.91:1, 320 to 1,440 pixels wide",
        postDaily:
          "Converts PNG and WebP to JPEG and resizes large images automatically",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Reel",
        network: "3 seconds to 15 minutes, up to 300 MB, H.264 or HEVC",
        postDaily: `Checks the length and warns over 300 MB; your plan caps each file at ${UPLOAD_CAP}`,
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Story video",
        network: "3 to 60 seconds, up to 100 MB",
        postDaily: "Checks the length and warns over 100 MB",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Collaborators",
        network: "Up to 3, on feed images, Reels and carousels",
        postDaily: "Up to 3, on an account connected through a Facebook Page",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "People tags",
        network: "Tagged by username, on images and videos",
        postDaily: "Up to 20 per post",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Alt text",
        network: "On feed and carousel images",
        postDaily: "1,000 characters",
        source: INSTAGRAM_MEDIA_REFERENCE,
        checkedOn: CHECKED,
      },
      {
        item: "Posts published by apps per day",
        network:
          "100 in a rolling 24 hours per the Rate Limit section; the same page says 50 under carousels. A carousel counts as one",
        postDaily:
          "Reads your account's live cap from Instagram and shows it in the composer",
        source: INSTAGRAM_CONTENT_PUBLISHING,
        checkedOn: CHECKED,
      },
      {
        item: "Product and location tags",
        network:
          "Shopping tags aren't supported for API publishing; location tags are",
        postDaily: "Neither is available yet",
        source: POSTDAILY_INSTAGRAM,
        checkedOn: CHECKED,
      },
    ],
    sections: [
      {
        heading: "Five hashtags, and a document that hasn't caught up",
        body: [
          "On 18 December 2025 Instagram's @creators account said it would gradually limit the hashtags in a post or Reel caption to five, down from 30, arguing that a few targeted hashtags beat many generic ones. Meta's developer reference for publishing still lists 30. Write to five: it is what the app now allows, and an API that later catches up will reject the rest.",
          "Whether hashtags in a comment, including a scheduled first comment, count toward the five is not something Instagram has said. We are testing it on our own account and will publish the result here rather than guess.",
        ],
      },
      {
        heading: "How many posts an app can publish in a day",
        body: [
          "Meta's content publishing guide contradicts itself. Its Rate Limit section says an account can publish 100 posts through the API in a rolling 24 hours; the carousel section of the same page says 50. Both agree a carousel counts as one post. A multi-image Story is different: it is published frame by frame, so each frame is a post of its own.",
          "Because the documents disagree, PostDaily doesn't assume a number. It asks Instagram for the account's current limit and usage, shows it in the composer, and when a post hits the cap it waits and retries once the 24-hour window moves on rather than failing it.",
        ],
      },
      {
        heading: "What PostDaily checks before a post is scheduled",
        body: [
          "The caption length, the carousel size against your plan, the Reel and Story video length, and the image format, which it converts to JPEG for you. It warns when a video is over Meta's documented size for its format. It doesn't count hashtags, and it can't add product or location tags yet. [Everything PostDaily can post to Instagram](/instagram-scheduler).",
        ],
      },
    ],
    faqs: [
      {
        q: "How long can an Instagram caption be?",
        a: "2,200 characters, on posts, carousels and Reels. Stories take no caption.",
      },
      {
        q: "How many hashtags can you use on Instagram?",
        a: "Five per post or Reel caption, since Instagram's change announced on 18 December 2025. It was 30 before that, and Meta's developer reference still says 30.",
      },
      {
        q: "How many photos can an Instagram carousel have?",
        a: "Ten images, videos or a mix, through Instagram's publishing API.",
      },
      {
        q: "How long can a Reel be when it's scheduled?",
        a: "From 3 seconds to 15 minutes, up to 300 MB, for Reels published through Instagram's API. A Story video runs 3 to 60 seconds.",
      },
      {
        q: "How many posts can a scheduler publish to Instagram a day?",
        a: "Meta's guide says 100 in a rolling 24 hours in one place and 50 in another. A carousel counts as one post; each Story frame counts on its own.",
      },
    ],
    landing: "/instagram-scheduler",
  },
  "youtube-shorts": {
    slug: "youtube-shorts",
    platform: "youtube",
    name: "YouTube Shorts",
    metaTitle: "YouTube Shorts Length, Size & Specs (2026) — PostDaily",
    metaDescription:
      "YouTube Shorts can run up to 3 minutes, square or vertical. Title, description and tag limits from YouTube's own docs, with the source for every number.",
    h1: "YouTube Shorts limits: length, shape and text",
    intro:
      "A Short is a square or vertical video of three minutes or less. Here is every limit that decides whether an upload lands as a Short, with the YouTube page each number comes from.",
    rows: [
      {
        item: "Length",
        network:
          "Up to 3 minutes (uploads from 15 October 2024; 60 seconds before that)",
        postDaily: "Warns when a Short runs over 3 minutes",
        source: YOUTUBE_SHORTS_HELP,
        checkedOn: CHECKED,
      },
      {
        item: "Shape",
        network: "Square (1:1) or vertical",
        postDaily:
          "Pre-selects Short for a vertical video of 3 minutes or less",
        source: YOUTUBE_SHORTS_HELP,
        checkedOn: CHECKED,
      },
      {
        item: "Title",
        network: "100 characters",
        postDaily: "100 characters; defaults to the post's first line",
        source: YOUTUBE_API,
        checkedOn: CHECKED,
      },
      {
        item: "Description",
        network:
          "5,000 bytes (UTF-8), so fewer characters with emoji or non-Latin text",
        postDaily: "5,000 bytes",
        source: YOUTUBE_API,
        checkedOn: CHECKED,
      },
      {
        item: "Tags",
        network: "500 characters for all tags together, commas included",
        postDaily: "500 characters together",
        source: YOUTUBE_API,
        checkedOn: CHECKED,
      },
      {
        item: "File size per upload",
        network: "256 GB or 12 hours, whichever is less",
        postDaily: UPLOAD_CAP,
        source: YOUTUBE_UPLOAD_HELP,
        checkedOn: CHECKED,
      },
    ],
    sections: [
      {
        heading: "What makes YouTube treat an upload as a Short",
        body: [
          "There is no Short checkbox in YouTube's upload API. YouTube decides from the file: a square or vertical video of three minutes or less is a Short. Adding **#Shorts** to the title or description helps it along, which is why PostDaily adds the tag when you choose Short.",
          "A horizontal video is a regular video whatever its length, and a vertical video over three minutes is a regular video too. Copyrighted music used to be a catch: a Short over one minute with an active Content ID claim was blocked. Since 24 September 2026, YouTube says such Shorts are no longer blocked automatically and may stay playable.",
        ],
      },
      {
        heading: "Why the description limit is in bytes",
        body: [
          "YouTube caps the description at 5,000 bytes, not 5,000 characters. English letters are one byte each, but an emoji takes four and most non-Latin characters take two or three, so a description in Japanese or full of emoji fits far fewer characters. PostDaily counts bytes for the same reason, so the counter agrees with YouTube.",
        ],
      },
      {
        heading: "PostDaily's own ceiling",
        body: [
          `Uploads through PostDaily are capped per file by plan: ${UPLOAD_CAP}. A Short fits comfortably; a long, high-bitrate video may not, and those are better uploaded in YouTube Studio.`,
        ],
      },
    ],
    faqs: [
      {
        q: "How long can a YouTube Short be?",
        a: "Up to three minutes, for videos uploaded on or after 15 October 2024. Before that the limit was 60 seconds.",
      },
      {
        q: "What aspect ratio do YouTube Shorts need?",
        a: "Square (1:1) or vertical. A horizontal video is uploaded as a regular video, whatever its length.",
      },
      {
        q: "How many characters can a YouTube Short title have?",
        a: "100 characters, the same as any YouTube video title.",
      },
      {
        q: "Why won't my description fit when it's under 5,000 characters?",
        a: "Because YouTube counts 5,000 bytes, not characters. Emoji and non-Latin text take two to four bytes each.",
      },
    ],
    landing: "/youtube-scheduler",
  },
  tiktok: {
    slug: "tiktok",
    platform: "tiktok",
    name: "TikTok",
    metaTitle: "TikTok Video Size, Length & Caption Limits (2026) — PostDaily",
    metaDescription:
      "TikTok's limits for apps that post for you: video size, length, resolution and frame rate, caption and photo-post limits, with TikTok's developer docs cited.",
    h1: "TikTok limits for videos, captions and photo posts",
    intro:
      "What TikTok accepts from a scheduling app is not quite what the TikTok app lets you do. These are the limits of TikTok's Content Posting API, the route every scheduler uses, with the TikTok page each comes from.",
    rows: [
      {
        item: "Video file size",
        network: "Up to 4 GB",
        postDaily: `4 GB, within your plan's per-file cap (${UPLOAD_CAP})`,
        source: TIKTOK_MEDIA,
        checkedOn: CHECKED,
      },
      {
        item: "Video length",
        network:
          "Up to 10 minutes from an app; TikTok may trim it to your account's own maximum",
        postDaily: "Not checked before publishing",
        source: TIKTOK_MEDIA,
        checkedOn: CHECKED,
      },
      {
        item: "Video format",
        network:
          "MP4 (recommended), WebM or MOV; H.264 (recommended), H.265, VP8 or VP9",
        postDaily: "MP4, MOV or WebM",
        source: TIKTOK_MEDIA,
        checkedOn: CHECKED,
      },
      {
        item: "Resolution and frame rate",
        network: "360 to 4,096 pixels each side; 23 to 60 frames per second",
        source: TIKTOK_MEDIA,
        checkedOn: CHECKED,
      },
      {
        item: "Caption",
        network: "2,200 characters (UTF-16) on videos",
        postDaily: "2,200 characters, with a warning from 2,100",
        source: TIKTOK_DIRECT_POST,
        checkedOn: CHECKED,
      },
      {
        item: "Photos per post",
        network: "Up to 35",
        postDaily: `Your plan's media per post: ${MEDIA_PER_POST}`,
        source: TIKTOK_PHOTO_POST,
        checkedOn: CHECKED,
      },
      {
        item: "Photo post title",
        network: "90 characters",
        postDaily: "The caption's first 90 characters",
        source: TIKTOK_PHOTO_POST,
        checkedOn: CHECKED,
      },
      {
        item: "Photo file",
        network: "JPEG or WebP, up to 20 MB, up to 1080p",
        postDaily: "Converts PNG and downsizes larger photos automatically",
        source: TIKTOK_MEDIA,
        checkedOn: CHECKED,
      },
    ],
    sections: [
      {
        heading: "App limits and API limits are different",
        body: [
          "The TikTok app lets you record, trim and add a sound from its library before posting. A scheduling app can't do any of that: it sends a finished file through TikTok's Content Posting API, and the API has its own limits, listed above. TikTok's docs also say it may trim a video sent by an app to fit your account's maximum length.",
          "Library sounds are the biggest gap. No app can attach them, so a video that needs one has to be finished in TikTok. PostDaily's **Get a reminder** option sends the video to your TikTok inbox as a draft for exactly that.",
        ],
      },
      {
        heading: "Why some schedulers' TikToks come out private",
        body: [
          "TikTok audits every app that posts through its API. Until an app passes, TikTok's own docs say everything it posts is restricted to private viewing, so a scheduler that hasn't been audited can only publish TikToks that nobody else sees. PostDaily passed the audit on 23 August 2026.",
        ],
      },
      {
        heading: "What PostDaily checks, and what it doesn't",
        body: [
          "PostDaily checks the caption length, the photo format and size, and the video file size before a post is scheduled. It does **not** yet check a video's length against your account's maximum, so a long video can still be refused by TikTok at publish time. [Check a video before you schedule it](/tools/video-checker).",
        ],
      },
    ],
    faqs: [
      {
        q: "What is TikTok's maximum video size?",
        a: "4 GB for videos posted through TikTok's API, which is what scheduling apps use.",
      },
      {
        q: "How long can a TikTok video be when it's scheduled?",
        a: "TikTok's API accepts up to 10 minutes, and TikTok may trim it to your account's own maximum. Accounts differ, so check yours in the app.",
      },
      {
        q: "How long can a TikTok caption be?",
        a: "2,200 characters for a video posted through the API. A photo post's title is 90 characters.",
      },
      {
        q: "How many photos can a TikTok photo post have?",
        a: "Up to 35 through TikTok's API.",
      },
    ],
    landing: "/tiktok-scheduler",
  },
  pinterest: {
    slug: "pinterest",
    platform: "pinterest",
    name: "Pinterest",
    metaTitle: "Pinterest Pin Size, Video & Text Limits (2026) — PostDaily",
    metaDescription:
      "Pinterest Pin sizes and limits: 1000×1500 (2:3) images, video Pin length and size, carousels of 2–5 images, title and description limits, with sources.",
    h1: "Pinterest Pin size and limits",
    intro:
      "The right Pin size is 1000×1500 pixels, a 2:3 ratio. Here is that and every other Pinterest limit, from Pinterest's own product specs.",
    rows: [
      {
        item: "Image Pin size",
        network:
          "2:3, or 1000×1500 pixels, recommended; taller than 2:3 may be cut off in the feed",
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Image file",
        network: "PNG or JPEG, up to 20 MB (32 MB in the app)",
        postDaily: `JPEG, PNG or WebP, within your plan's per-file cap (${UPLOAD_CAP})`,
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Video Pin length",
        network: "4 seconds to 15 minutes; 6 to 15 seconds recommended",
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Video Pin file",
        network: "MP4, MOV or M4V, up to 2 GB",
        postDaily: `MP4 or MOV, within your plan's per-file cap (largest ${LARGEST_UPLOAD})`,
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Video Pin shape",
        network:
          "Square or vertical (2:3, 4:5, 9:16) recommended; between 1.91:1 and 1:2",
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Carousel",
        network: "2 to 5 images, up to 20 MB each, all 1:1 or all 2:3",
        postDaily: `2 to 5 images, within your plan's media per post (${MEDIA_PER_POST})`,
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Title",
        network: "100 characters; about the first 40 show in feeds",
        postDaily: "100 characters",
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Description",
        network: "800 characters; not shown in feeds, used for search",
        postDaily: "800 characters (your post text)",
        source: PINTEREST_SPECS,
        checkedOn: CHECKED,
      },
      {
        item: "Alt text",
        network: "500 characters through the API",
        postDaily: "500 characters",
        source: POSTDAILY_API,
        checkedOn: CHECKED,
      },
    ],
    sections: [
      {
        heading: "Why 2:3 is the size to use",
        body: [
          "Pinterest's feed is a grid of columns with a fixed width, so a Pin's height is what changes. A 2:3 image (1000×1500 pixels) is the shape Pinterest recommends. A taller one may be cut off in the feed, and a square or landscape one simply takes up less room next to the Pins around it.",
        ],
      },
      {
        heading: "Video Pins need a cover",
        body: [
          "Pinterest's API asks for a cover image with every video Pin. PostDaily uses the first image attached to the post, so a video Pin needs one image alongside the video. Keep the video between 4 seconds and 15 minutes, and vertical or square.",
        ],
      },
      {
        heading: "Carousels",
        body: [
          "A carousel Pin holds 2 to 5 images, and Pinterest asks for them all to be the same shape: all square or all 2:3. How many fit in one PostDaily post also depends on your plan.",
        ],
      },
    ],
    faqs: [
      {
        q: "What size should a Pinterest Pin be?",
        a: "1000×1500 pixels, a 2:3 ratio, is what Pinterest recommends. Taller Pins may be cut off in the feed.",
      },
      {
        q: "How long can a Pinterest video be?",
        a: "From 4 seconds to 15 minutes. Pinterest recommends 6 to 15 seconds.",
      },
      {
        q: "How many characters can a Pin title have?",
        a: "100, though only about the first 40 show in the feed. The description can run to 800 characters.",
      },
      {
        q: "How many images can a Pinterest carousel have?",
        a: "2 to 5, all the same shape.",
      },
    ],
    landing: "/pinterest-scheduler",
  },
} as const satisfies Record<LimitsPage["slug"], LimitsPage>;

export const LIMITS_PAGE_LIST: LimitsPage[] = Object.values(LIMITS_PAGES);

export function getLimitsPage(slug: string): LimitsPage | undefined {
  return LIMITS_PAGE_LIST.find((page) => page.slug === slug);
}
